import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronsDownUp, ChevronsUpDown, ChevronRight } from 'lucide-react';
import { useI18n } from '@/i18n/I18nProvider';
import { usePermissionChecker } from '@/features/auth/permissions';
import { cn } from '@/shared/lib/cn';
import { accentForPath, hexToRgba } from '@/shared/lib/nav-accent';
import { HeroFreshness, HeroInsight, HeroQuickActions } from './hero/HeroExtras';
import { HeroStat, HeroStatSkeleton } from './hero/HeroStat';
import { useHeroStore } from './hero/hero.store';
import type { HeroInsightData, HeroQuickAction, HeroStatData } from './hero/types';

export interface Crumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Własna ikona; domyślnie ikona pozycji menu dla bieżącej ścieżki. */
  icon?: ReactNode;
  breadcrumbs?: Crumb[];
  /** Plakietki / liczniki obok tytułu (np. <HeroChip />). */
  meta?: ReactNode;
  actions?: ReactNode;
  /** Dodatkowa treść pod tytułem (np. pasek KPI w kafelkach HeroChip). */
  children?: ReactNode;
  /** Własny kolor akcentu (hex); domyślnie z menu. */
  accent?: string;
  /** Kafelki wskaźników (count-up, zmiana %, trend, cel) — „Hero 2.0”. */
  stats?: HeroStatData[];
  /** Gdy true, zamiast kafelków pokazuje szkielety (dane się ładują). */
  statsLoading?: boolean;
  /**
   * Własny układ siatki kafelków (zastępuje domyślne kolumny), np. stały 2×2 / 4×1 zależnie od szerokości kontenera
   * zamiast auto-fit, który przy 4 kafelkach potrafi dać 3 + 1.
   */
  statsClassName?: string;
  /** Wniosek w jednym zdaniu z przyciskiem akcji (null/undefined = brak). */
  insight?: HeroInsightData | null;
  /** Szybkie akcje (kapsuły) pod kafelkami. */
  quickActions?: HeroQuickAction[];
  /** Znacznik świeżości danych („Na żywo · aktualizacja 11:26”) i odświeżenie. */
  updatedAt?: Date | number | string;
  onRefresh?: () => void;
  refreshing?: boolean;
  className?: string;
}

/**
 * Nagłówek widoku w stylu ZUR OPS: baza w kolorze menu (granat), a kolor widoku jako akcent —
 * kapsuła z ikoną, poświata w rogu, górna linia i duża przezroczysta ikona w tle.
 */
export function PageHeader({
  title,
  description,
  icon,
  breadcrumbs,
  meta,
  actions,
  children,
  accent,
  className,
  stats,
  statsLoading,
  statsClassName,
  insight,
  quickActions,
  updatedAt,
  onRefresh,
  refreshing,
}: PageHeaderProps) {
  const { t } = useI18n();
  const compact = useHeroStore((st) => st.compact);
  const toggleCompact = useHeroStore((st) => st.toggle);
  const can = usePermissionChecker();
  const allowedActions = quickActions?.filter((a) => can(a.permission));
  const hasRich = Boolean(stats?.length || statsLoading || insight || allowedActions?.length || updatedAt || onRefresh);
  const { pathname } = useLocation();
  const nav = accentForPath(pathname);
  const color = accent ?? nav.color;
  const Ghost = nav.icon;

  return (
    <header
      className={cn('relative shrink-0 animate-fade-in overflow-hidden rounded-2xl px-5 py-4 text-white md:px-6 md:py-5', className)}
      style={{
        background: 'linear-gradient(135deg, hsl(var(--sidebar)) 0%, #16203a 65%, #1e293b 100%)',
        boxShadow: '0 8px 28px rgba(2,6,23,0.28)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(120% 95% at 93% 4%, ${hexToRgba(color, 0.5)}, transparent 55%)` }}
      />
      <div aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: color }} />
      <Ghost
        aria-hidden
        strokeWidth={1.25}
        className="pointer-events-none absolute -bottom-8 -right-6 size-44 opacity-[0.16]"
        style={{ color }}
      />

      <div className="relative flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav aria-label="breadcrumb" className="mb-1.5 flex items-center gap-1 text-xs text-white/55">
              {breadcrumbs.map((c, i) => (
                <span key={`${c.label}-${i}`} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="size-3 opacity-60" aria-hidden />}
                  {c.to ? (
                    <Link to={c.to} className="transition-colors hover:text-white">
                      {c.label}
                    </Link>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          <div className="flex min-w-0 items-center gap-3.5">
            <span
              className="flex size-12 shrink-0 items-center justify-center rounded-2xl text-white ring-1 ring-white/15 [&_svg]:size-6"
              style={{ background: color, boxShadow: `0 4px 18px ${hexToRgba(color, 0.55)}` }}
            >
              {icon ?? <nav.icon />}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="truncate text-xl font-extrabold leading-tight text-white">{title}</h1>
                {meta && <div className="flex max-w-full flex-wrap items-center gap-1.5">{meta}</div>}
              </div>
              {description && <p className="mt-0.5 text-[12px] text-white/55">{description}</p>}
            </div>
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 md:max-w-[62%] md:justify-end">{actions}</div>}
      </div>

      {children && <div className="relative mt-4">{children}</div>}

      {hasRich && !compact && (
        <div className="relative mt-3 space-y-2.5">
          {(stats?.length || statsLoading) && (
            <div
              className={cn(
                'grid gap-2 sm:gap-2.5',
                statsClassName ?? 'grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(12.5rem,1fr))]',
              )}
            >
              {statsLoading && !stats?.length
                ? [0, 1, 2, 3].map((i) => <HeroStatSkeleton key={i} />)
                : stats!.map((st, i) => <HeroStat key={st.key} stat={st} accent={color} index={i} />)}
            </div>
          )}
          {insight && <HeroInsight insight={insight} />}
          {(allowedActions?.length || updatedAt || onRefresh) && (
            <div className="no-print flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              {allowedActions?.length ? <HeroQuickActions actions={allowedActions} /> : <span />}
              <HeroFreshness updatedAt={updatedAt} onRefresh={onRefresh} refreshing={refreshing} />
            </div>
          )}
        </div>
      )}

      {hasRich && (
        <button
          type="button"
          onClick={toggleCompact}
          aria-pressed={compact}
          title={compact ? t('hero.expand') : t('hero.collapse')}
          aria-label={compact ? t('hero.expand') : t('hero.collapse')}
          className="no-print absolute bottom-2 right-2 z-10 flex size-6 items-center justify-center rounded-md text-white/45 transition-colors hover:bg-white/10 hover:text-white"
        >
          {compact ? <ChevronsUpDown className="size-3.5" /> : <ChevronsDownUp className="size-3.5" />}
        </button>
      )}
    </header>
  );
}

/** Licznik / plakietka w pasku nagłówka widoku (np. „Otwarte: 12”). */
export function HeroChip({ label, value, dot, title }: { label: ReactNode; value?: ReactNode; dot?: string; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-2 rounded-xl bg-white/[0.14] px-3 py-1.5 text-[11px] font-medium text-white"
    >
      {dot && <span aria-hidden className="size-2 rounded-full" style={{ background: dot }} />}
      <span className="text-white/70">{label}</span>
      {value !== undefined && <span className="font-extrabold tabular-nums">{value}</span>}
    </span>
  );
}

/** Standard page container with responsive padding. */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mx-auto w-full max-w-[1600px] space-y-5 p-4 md:p-6', className)}>{children}</div>;
}
